<script setup lang="ts">
import { MAIN_NAV_ITEMS } from "@/config/nav";
import { useUserStore } from "@/stores/user";
import { Bell, Message } from "@element-plus/icons-vue";
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useRouter } from "vue-router";

// 导入图片资源
import icoIn1 from "@/assets/images/ico-in-1.svg";
import icoIn2 from "@/assets/images/ico-in-2.svg";
import icoIn3 from "@/assets/images/ico-in-3.svg";
import icoIn4 from "@/assets/images/ico-in-4.svg";
import icoOut1 from "@/assets/images/ico-out-1.svg";
import icoOut2 from "@/assets/images/ico-out-2.svg";
import icoOut3 from "@/assets/images/ico-out-3.svg";
import logo from "@/assets/images/logo.svg";
import userAvatar from "@/assets/images/user-avatar.png";

const router = useRouter();
const userStore = useUserStore();

// 响应式状态
const isMobileMenuOpen = ref(false);
// 是否为移动设备视图（<=1200px）
// 在SSR环境中，默认设为false，在客户端再更新
const isMobileView = ref(false);

// 常量定义 - 从配置文件导入导航项
const NAV_ITEMS = MAIN_NAV_ITEMS;

// 计算属性
const activePath = computed(() => {
  return router.currentRoute.value.path.split("/")[1] || "";
});

// 工具函数
const closeMobileMenuOnAction = () => {
  if (isMobileMenuOpen.value) {
    closeMobileMenus();
  }
};

const setBodyScroll = (disabled: boolean) => {
  if (typeof document !== "undefined") {
    document.body.style.overflow = disabled ? "hidden" : "";
  }
};

// 核心方法
const handleNavClick = (path: string) => {
  // 判断是否为外部链接
  if (path.startsWith('http://') || path.startsWith('https://')) {
    window.open(path, '_blank');
  } else {
    router.push(path);
  }
  closeMobileMenuOnAction();
};

const handleLoginClick = () => {
  router.push("/login");
  closeMobileMenuOnAction();
};

const handleLogoutClick = () => {
  userStore.logout();
  router.push("/login");
  closeMobileMenuOnAction();
};

const toggleMobileMenus = () => {
  isMobileMenuOpen.value ? closeMobileMenus() : openMobileMenus();
};

const openMobileMenus = () => {
  if (!isMobileView.value) return;

  isMobileMenuOpen.value = true;
  setBodyScroll(true);
};

const closeMobileMenus = () => {
  isMobileMenuOpen.value = false;
  setBodyScroll(false);
};

// 响应式处理
const checkViewport = () => {
  if (typeof window !== "undefined") {
    isMobileView.value = window.innerWidth <= 1200;

    // 如果切换到桌面端且菜单打开，自动关闭菜单
    if (!isMobileView.value && isMobileMenuOpen.value) {
      closeMobileMenus();
    }
  }
};

// 键盘事件处理
const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === "Escape" && isMobileMenuOpen.value) {
    closeMobileMenus();
  }
};

// 生命周期
onMounted(() => {
  if (typeof window !== "undefined") {
    // 初始化移动设备状态
    isMobileView.value = window.innerWidth <= 1200;
    window.addEventListener("resize", checkViewport);
    window.addEventListener("keydown", handleKeydown);
  }
});

onUnmounted(() => {
  if (typeof window !== "undefined") {
    window.removeEventListener("resize", checkViewport);
    window.removeEventListener("keydown", handleKeydown);
  }
  setBodyScroll(false); // 清理状态
});

// 路由监听
router.afterEach(closeMobileMenuOnAction);
</script>

<template>
  <header class="header">
    <!-- Logo -->
    <div class="header-logo" @click="handleNavClick('/')">
      <img :src="logo" alt="Logo" class="cp-image" />
    </div>
    <!-- 导航 -->
    <nav class="header-nav">
      <ul class="cp-list">
        <li
          v-for="item in NAV_ITEMS"
          :key="item.path"
          class="cp-item"
          :class="{ active: activePath === item.path.split('/')[1] }"
          @click="handleNavClick(item.path)"
        >
          {{ item.label }}
        </li>
      </ul>
    </nav>
    <!-- 用户操作区 -->
    <div class="header-user">
      <!-- 通知图标 -->
      <div class="cp-icon notification-icon">
        <el-icon>
          <Message />
        </el-icon>
      </div>

      <div class="cp-icon notification-icon">
        <el-icon>
          <Bell />
        </el-icon>
      </div>
      <!-- ./ -->
      <div
        class="header-navbar"
        :class="{ active: isMobileMenuOpen }"
        @click="toggleMobileMenus"
      >
        <span></span>
        <span></span>
        <span></span>
      </div>
      <!-- ./ -->

      <!-- 用户头像/登录按钮 -->
      <div class="cp-actions">
        <!-- 头像下拉 -->
        <el-dropdown placement="bottom-end">
          <img :src="userAvatar" alt="用户头像" class="cp-avatar" />
          <template #dropdown>
            <div class="user-box">
              <div class="cp-card">
                <img :src="userAvatar" alt="用户头像" class="cp-avatar" />
                <div class="cp-info">
                  <div class="cp-name">王翎旭</div>
                  <div class="cp-position">上海｜设计爱好者</div>
                </div>
              </div>
              <div class="cp-logout-list">
                <div class="cp-item">
                  <img :src="icoIn1" />
                  <p>账号管理</p>
                </div>
                <div class="cp-item">
                  <img :src="icoIn2" />
                  <p>资料管理</p>
                </div>
                <div class="cp-item">
                  <img :src="icoIn3" />
                  <p>课题管理</p>
                </div>
                <div class="cp-item">
                  <img :src="icoIn4" />
                  <p>作品管理</p>
                </div>
              </div>
              <el-button class="cp-logout-btn" @click.stop="handleLogoutClick">
                退出账号
              </el-button>
            </div>
          </template>
        </el-dropdown>
        <!-- 登录/注册下拉 -->
        <el-dropdown placement="bottom-end">
          <el-button class="cp-login-btn" type="primary">登录/注册</el-button>
          <template #dropdown>
            <div class="user-box">
              <div class="cp-login-tips">
                <div class="cp-link" @click.stop="handleLoginClick">登录/注册</div>
                后，可享受：
              </div>
              <div class="cp-login-list">
                <div class="cp-item">
                  <img :src="icoOut1" />
                  参与联合毕设
                </div>
                <div class="cp-item">
                  <img :src="icoOut2" />
                  分享你的作品
                </div>
                <div class="cp-item">
                  <img :src="icoOut3" />
                  直投企业简历
                </div>
              </div>
              <el-button
                class="cp-login-btn"
                type="primary"
                @click.stop="handleLoginClick"
              >
                登录/注册
              </el-button>
            </div>
          </template>
        </el-dropdown>
      </div>
    </div>
  </header>
  <div class="header-spacer"></div>
  <!-- ./ -->
  <div
    class="mobile-fix"
    :class="{ 'mobile-fix--open': isMobileMenuOpen }"
    @click="closeMobileMenus"
  >
    <div class="mobile-menus" @click.stop>
      <ul>
        <li
          v-for="item in NAV_ITEMS"
          :key="item.path"
          @click="handleNavClick(item.path)"
          :class="{ active: activePath === item.path.split('/')[1] }"
        >
          {{ item.label }}
        </li>
      </ul>
    </div>
    <div class="mobile-lang"></div>
  </div>
</template>

<style lang="scss" scoped>
.header {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  width: 100%;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 50px;
  height: 60px;
  background-color: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.header-spacer {
  height: 60px;
}

/* Logo样式 */
.header-logo {
  display: flex;
  align-items: center;
  cursor: pointer;
  .cp-logo {
    width: auto;
    height: 60px;
  }
}

/* 桌面端导航 */
.header-nav {
  display: flex;
  align-items: center;
  .cp-list {
    display: flex;
    list-style: none;
    margin: 0;
    padding: 0;
    gap: 40px;
    .cp-item {
      height: 60px;
      line-height: 60px;
      transition: all 0.3s ease;
      font-size: 14px;
      color: #333;
      border-radius: 4px;
      position: relative;
      cursor: pointer;
      &:hover {
        color: var(--primary-color);
      }
      &.active {
        color: var(--primary-color);
        &::after {
          content: "";
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 100%;
          height: 2px;
          background-color: var(--primary-color);
          border-radius: 1px;
        }
      }
    }
  }
}

/* 用户操作区 */
.header-user {
  display: flex;
  align-items: center;
  .cp-icon {
    margin-right: 10px;
    display: none;
    line-height: 1;
    color: #666;
    position: relative;
    padding: 6px 6px 0 0;
    border-radius: 4px;
    transition: all 0.3s ease;
    font-size: 24px;
    &:hover {
      color: var(--primary-color);
      background-color: #f0f9ff;
    }
    &.notification-icon::after {
      content: "";
      position: absolute;
      top: 6px;
      right: 6px;
      width: 6px;
      height: 6px;
      background-color: #ff4d4f;
      border-radius: 50%;
    }
  }
  .cp-actions {
    display: flex;
    align-items: center;
    display: none;
    .cp-avatar {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      object-fit: cover;
    }
  }
}

.user-box {
  padding: 16px;
}
.cp-card {
  display: flex;
  align-items: center;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
  .cp-avatar {
    width: 44px;
    height: 44px;
    margin-right: 8px;
    border-radius: 50%;
    object-fit: cover;
  }
  .cp-info {
    .cp-name {
      font-size: 16px;
      color: #333;
      color: var(--primary-color);
    }
    .cp-position {
      margin-top: 3px;
      font-size: 14px;
      color: #666;
    }
  }
}

.cp-logout-list {
  width: 200px;
  display: flex;
  flex-wrap: wrap;
  margin-top: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
  margin-bottom: 15px;
  .cp-item {
    width: 100px;
    padding: 8px 4px;
    font-size: 14px;
    color: #666;
    cursor: pointer;
    text-align: center;
    transition: all 0.3s ease;
    border-radius: 10px;
    &:hover {
      background-color: #f7f8fa;
    }
    img {
      display: block;
      margin: 0 auto;
    }
    p {
      margin-top: 8px;
    }
  }
}

/* 下拉菜单样式 */

.cp-login-tips {
  display: flex;
  align-items: center;
  font-size: 16px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
  .cp-link {
    padding: 0;
    font-size: 16px;
    line-height: 1.2;
    color: var(--primary-color);
    cursor: pointer;
  }
}

.cp-login-list {
  .cp-item {
    margin-top: 15px;
    display: flex;
    margin-bottom: 16px;
    font-size: 16px;
    line-height: 1.2;
    img {
      width: 24px;
      height: 24px;
      margin-right: 8px;
    }
  }
}

.cp-logout-btn,
.cp-login-btn {
  width: 100%;
  border-radius: 10px;
}

/* */
.header-navbar {
  width: 20px;
  cursor: pointer;
  display: none;
  position: relative;
}

.header-navbar span {
  display: block;
  width: 100%;
  height: 2px;
  background-color: #333;
  transition: all 0.3s ease;
  transform-origin: center;
}

.header-navbar span:nth-child(2) {
  position: relative;
  margin: 5px 0;
}

/* 汉堡菜单激活状态样式 */
.header-navbar.active span:nth-child(1) {
  transform: rotate(45deg);
  transform-origin: 0 0;
}

.header-navbar.active span:nth-child(2) {
  width: 0;
}

.header-navbar.active span:nth-child(3) {
  transform: rotate(-45deg);
  transform-origin: 0 100%;
}
// 移动端导航栏
.mobile-fix {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999;
  width: 100%;
  height: 100%;
  transition: all 0.3s ease;
  opacity: 0;
  visibility: hidden;
}

.mobile-fix--open {
  opacity: 1;
  visibility: visible;
}

.mobile-menus {
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  background-color: #f7f8fa;
  padding: 80px 20px 30px;
  overflow-y: auto;
  transform: translateY(-100%);
  transition: transform 0.3s ease;
}

.mobile-fix--open .mobile-menus {
  transform: translateX(0);
}

.mobile-menus ul {
  list-style: none;
  margin: 0;
  padding: 0;
}

.mobile-menus ul li {
  font-size: 16px;
  color: #333;
  cursor: pointer;
  padding: 15px 0;
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.3s ease;

  &:hover {
    color: var(--primary-color);
    background-color: #f8f9fa;
    padding-left: 10px;
  }

  &.active {
    color: var(--primary-color);
    position: relative;
  }

  &.mobile-user-section {
    border-bottom: none;
    margin-top: 20px;
    padding: 20px 0;
    border-top: 1px solid #eee;
  }
}

.mobile-user-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  .cp-icon {
    font-size: 20px;
    color: #666;
    padding: 8px;
    border-radius: 6px;
    transition: all 0.3s ease;

    &:hover {
      color: var(--primary-color);
      background-color: #f0f9ff;
    }
  }

  .cp-login-btn {
    flex: 1;
    border-radius: 6px;
    font-size: 14px;
    padding: 8px 12px;
  }
}

/* 响应式 */

@media (max-width: 1440px) {
  .header-nav {
    .cp-list {
      gap: 30px;
    }
  }
}

@media (max-width: 1366px) {
  .header {
    padding: 0 15px;
  }
  .header-nav {
    .cp-list {
      gap: 20px;
    }
  }
}

@media (max-width: 1200px) {
  .header-nav {
    display: none;
  }
  .header-user {
    .cp-actions {
      display: none;
    }
  }

  .header-navbar {
    display: block;
  }
}
</style>
